import { z } from 'zod';
import { attachment, dateStr, objectIdStr } from './common.js';

/**
 * A "special item" pinned to the top of a row's description — structured facts the
 * free-text description shouldn't have to carry in prose. Deliberately not editable:
 * a wrong one is deleted and re-added, so what is recorded is always what somebody
 * meant to record. `kind` is a union of one for now; the picker behind the ⊕ in the
 * description cell is built to grow.
 */
export const listItemMark = z.object({
  _id: objectIdStr,
  kind: z.literal('dateRequested'),
  /** The date being recorded — for `dateRequested`, the day the request came in. */
  date: dateStr,
  /** Who asked. Optional: often the date alone is the whole point. */
  by: z.string().optional(),
  addedAt: z.coerce.date(),
  addedBy: z.string().optional(),
});
export type ListItemMark = z.infer<typeof listItemMark>;
export type ListItemMarkKind = ListItemMark['kind'];

export const listItemMarkCreate = z.object({
  kind: z.literal('dateRequested'),
  date: dateStr,
  by: z.string().trim().min(1).optional(),
});
export type ListItemMarkCreate = z.infer<typeof listItemMarkCreate>;

/**
 * One row on a job's "List" page — a todo/notes hybrid the customer reads far more
 * often than the plan. Lives in its own collection (like `parts`/`scans`), not
 * embedded on the job, because it is richer and more numerous than a `JobNote`.
 */
export const listItem = z.object({
  _id: objectIdStr,
  jobId: objectIdStr,
  work: z.string().min(1),
  /** Free-form and multi-line — the row's notes, not just a one-line label. */
  description: z.string().optional(),
  assignedTo: z.string().optional(),
  neededBy: dateStr.optional(),
  done: z.boolean().default(false),
  /** Newest first, the way they read on the row. */
  marks: z.array(listItemMark).default([]),
  attachments: z.array(attachment).default([]),
  createdAt: z.coerce.date(),
  createdBy: z.string().optional(),
  updatedAt: z.coerce.date().optional(),
});
export type ListItem = z.infer<typeof listItem>;

export const listItemCreate = z.object({
  work: z.string().trim().min(1),
  description: z.string().trim().min(1).optional(),
  assignedTo: z.string().trim().min(1).optional(),
  neededBy: dateStr.optional(),
});
export type ListItemCreate = z.infer<typeof listItemCreate>;

/** `null` on an optional field clears it. */
export const listItemPatch = z.object({
  work: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).nullable().optional(),
  assignedTo: z.string().trim().min(1).nullable().optional(),
  neededBy: dateStr.nullable().optional(),
  done: z.boolean().optional(),
});
export type ListItemPatch = z.infer<typeof listItemPatch>;
